/**
 * Public forum moderation page. Mirrors the voicemails inline-player +
 * DataTable shape:
 *  - PageHeader with status counts
 *  - Filter chips (Pending / Approved / Rejected / All)
 *  - One row per message; clicking play swaps the from-number cell
 *    for the compact AudioPlayer so only the clicked row loads bytes
 *  - Approve / Reject / Delete inline; pending-only bulk approve/reject
 *
 * Approve threads the post-mode node's `config.folder_id` (if any)
 * through to the backend so the underlying recording lands in that
 * folder atomically with the moderation flip — keeps the bulletin
 * the customer wired into the rest of the IVR in sync.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { forum } from '@/api/forum';
import { ivrNodes } from '@/api/ivrNodes';
import { recordings } from '@/api/recordings';
import type { ForumMessageRow, ModerationStatus } from '@/api/forum';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Check, X, Trash2, Play, Square, Megaphone, Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';

const CAN_MODERATE = ['super_admin', 'reseller_owner', 'office_manager'];

const TABS = [
  { key: 'pending', label: 'Pending', icon: Clock, tone: 'amber' },
  { key: 'approved', label: 'Approved', icon: CheckCircle2, tone: 'emerald' },
  { key: 'rejected', label: 'Rejected', icon: XCircle, tone: 'rose' },
  { key: 'all', label: 'All', icon: Megaphone, tone: 'slate' },
] as const;
type TabKey = typeof TABS[number]['key'];

function durationLabel(ms: number | null): string {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return 'just now';
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function ForumModeration() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canModerate = CAN_MODERATE.includes(user.role);
  const [tab, setTab] = useState<TabKey>('pending');

  // Optional ?node=<uuid> scopes the entire page to a single
  // public_forum node. Used by the "View forum messages" link on
  // each public_forum node editor — operators with multiple forum
  // nodes per customer get a per-forum moderation view without us
  // needing separate routes (Sam 2026-05-20).
  const [searchParams] = useSearchParams();
  const nodeId = searchParams.get('node') || undefined;

  const { data: statsData } = useQuery({
    queryKey: ['forum-stats', effectiveCustomerId, nodeId],
    queryFn: () => forum.stats({ node_id: nodeId }),
  });
  const stats = statsData ?? { pending: 0, approved: 0, rejected: 0 };

  // Load the node row (for the "filtered to" badge in the header)
  // only when a node filter is set.
  const { data: nodeRow } = useQuery({
    queryKey: ['forum-node', nodeId],
    queryFn: () => ivrNodes.get(nodeId!),
    enabled: !!nodeId,
  });
  const nodeLabel = nodeRow
    ? `IVR-${String(nodeRow.ref_number).padStart(4, '0')} ${nodeRow.name ?? ''}`.trim()
    : null;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Engagement"
        title={nodeLabel ? `Public forum — ${nodeLabel}` : 'Public forum'}
        description={
          nodeLabel
            ? `Showing messages posted from this forum node only. Remove the ?node= URL param to see every forum.`
            : 'Caller-recorded messages. Approved messages play in the IVR listen node; rejected stay hidden but auditable.'
        }
      />

      <FilterChips
        tab={tab}
        onChange={setTab}
        counts={{
          pending: stats.pending,
          approved: stats.approved,
          rejected: stats.rejected,
          all: stats.pending + stats.approved + stats.rejected,
        }}
      />

      <MessagesTable
        tab={tab}
        canModerate={canModerate}
        customerId={effectiveCustomerId}
        nodeId={nodeId}
      />
    </div>
  );
}

function FilterChips({
  tab, onChange, counts,
}: {
  tab: TabKey;
  onChange: (t: TabKey) => void;
  counts: Record<TabKey, number>;
}) {
  return (
    <div
      className="flex flex-wrap gap-2"
      role="tablist"
      aria-label="Filter forum messages by status"
    >
      {TABS.map((t) => {
        const Icon = t.icon;
        const active = tab === t.key;
        const tones: Record<string, { bg: string; text: string; ring: string }> = {
          amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-300' },
          emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-300' },
          rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-300' },
          slate:   { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-300' },
        };
        const tone = tones[t.tone] ?? tones.slate!;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-xs font-medium transition-all',
              active
                ? `${tone.bg} ${tone.text} border-transparent ring-2 ${tone.ring}`
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{t.label}</span>
            <span
              className={cn(
                'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-mono tabular-nums',
                active ? 'bg-white/70' : 'bg-slate-100',
              )}
            >
              {counts[t.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MessagesTable({
  tab, canModerate, customerId, nodeId,
}: {
  tab: TabKey;
  canModerate: boolean;
  customerId: string | null;
  nodeId?: string;
}) {
  const qc = useQueryClient();
  const accessToken = useAuth((s) => s.accessToken);
  const queryStatus: ModerationStatus | undefined =
    tab === 'all' ? undefined : tab;
  const { data, isLoading } = useQuery({
    queryKey: ['forum-messages', tab, customerId, nodeId],
    queryFn: () => forum.list({
      status: queryStatus,
      limit: 200,
      customer_id: customerId ?? undefined,
      node_id: nodeId,
    }),
  });
  const rows = data?.rows ?? [];

  // Per-row playback state. The moderation player streams via
  // /api/recordings/<recording_id>/audio?token=<jwt> (same endpoint the
  // recordings library uses) — works for both local-bucket and
  // S3/R2-bucket recordings without needing a presigned URL. The old
  // /forum-messages/:id/playback endpoint returned 501 for local-bucket
  // forum recordings (Sam 2026-05-20).
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Per-row selection for bulk actions.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['forum-messages'] });
    qc.invalidateQueries({ queryKey: ['forum-stats'] });
  };

  // Approve threads through the node's configured folder. We use a
  // map of nodeId → folder_id loaded on-demand from the IvrNode rows
  // already visible in the table.
  const nodeIds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.ivr_node_id).filter(Boolean) as string[])),
    [rows],
  );
  const nodeQueries = useQuery({
    queryKey: ['forum-nodes-folders', nodeIds.join(',')],
    queryFn: async () => {
      const out: Record<string, string | null> = {};
      await Promise.all(nodeIds.map(async (id) => {
        try {
          const node = await ivrNodes.get(id);
          const cfg = (node.config ?? {}) as { folder_id?: string | null };
          out[id] = cfg.folder_id ?? null;
        } catch {
          out[id] = null;
        }
      }));
      return out;
    },
    enabled: nodeIds.length > 0,
    staleTime: 30_000,
  });
  const folderByNode = nodeQueries.data ?? {};

  const approve = useMutation({
    mutationFn: (p: { id: string; folderId: string | null }) =>
      forum.moderate(p.id, 'approved', p.folderId),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (id: string) => forum.moderate(id, 'rejected'),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => forum.remove(id),
    onSuccess: (_d, id) => {
      setPlayingId((p) => (p === id ? null : p));
      invalidate();
    },
  });
  // Bulk actions — run sequentially so a failure surfaces clearly.
  const bulkApprove = useMutation({
    mutationFn: async () => {
      for (const id of Array.from(selected)) {
        const m = rows.find((r) => r.id === id);
        const folderId = m?.ivr_node_id ? folderByNode[m.ivr_node_id] ?? null : null;
        await forum.moderate(id, 'approved', folderId);
      }
    },
    onSuccess: () => { setSelected(new Set()); invalidate(); },
  });
  const bulkReject = useMutation({
    mutationFn: async () => {
      for (const id of Array.from(selected)) {
        await forum.moderate(id, 'rejected');
      }
    },
    onSuccess: () => { setSelected(new Set()); invalidate(); },
  });

  // Toggle play/stop. The URL is built synchronously from the
  // recording_id + access token — no lazy fetch needed.
  function togglePlay(messageId: string): void {
    setAudioError(null);
    if (playingId === messageId) { setPlayingId(null); return; }
    setPlayingId(messageId);
  }

  const allSelectedOnPage = rows.length > 0 && rows.every((m) => selected.has(m.id));
  const someSelectedOnPage = rows.some((m) => selected.has(m.id));

  function toggleAllOnPage(checked: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) rows.forEach((m) => next.add(m.id));
      else rows.forEach((m) => next.delete(m.id));
      return next;
    });
  }
  function toggleOne(id: string, checked: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }

  if (isLoading) {
    return <div className="text-sm text-slate-400">Loading…</div>;
  }
  if (rows.length === 0) {
    return <EmptyState tab={tab} />;
  }

  return (
    <section className="space-y-3">
      {/* Bulk action bar — shows the select-all + the actions when at
          least one row is selected. Mirrors VoicemailBoxDetail. */}
      {canModerate && (
        <div className="flex flex-wrap items-center gap-2">
          <Checkbox
            checked={allSelectedOnPage}
            data-state={!allSelectedOnPage && someSelectedOnPage ? 'indeterminate' : undefined}
            onCheckedChange={(v) => toggleAllOnPage(Boolean(v))}
            aria-label="Select all on page"
          />
          <span className="text-xs text-muted-foreground">
            {selected.size > 0 ? `${selected.size} selected` : 'Select all on page'}
          </span>
          {selected.size > 0 && tab === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => bulkApprove.mutate()}
                disabled={bulkApprove.isPending}
                className="h-7 bg-emerald-700 hover:bg-emerald-800"
              >
                <Check className="me-1 h-3.5 w-3.5" />
                Approve {selected.size}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkReject.mutate()}
                disabled={bulkReject.isPending}
                className="h-7 border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <X className="me-1 h-3.5 w-3.5" />
                Reject {selected.size}
              </Button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700"
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {audioError && (
        <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700" role="alert">
          {audioError}
        </div>
      )}

      <div className="rounded-md border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              {canModerate && <TableHead className="w-8" />}
              <TableHead>Caller</TableHead>
              {!nodeId && <TableHead>Forum</TableHead>}
              <TableHead>Posted</TableHead>
              <TableHead>Length</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-end">Plays</TableHead>
              <TableHead className="w-48 text-end">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const isPlaying = playingId === r.id;
              const audioOk = !!r.recording_id;
              const callerLabel =
                r.caller_display_name || r.posted_from_e164 || 'Anonymous';
              const forumLabel = r.ivr_node_ref !== null
                ? `IVR-${String(r.ivr_node_ref).padStart(4, '0')}`
                : '—';
              return (
                <Fragment key={r.id}>
                  <TableRow className={cn('transition-colors', isPlaying && 'bg-blue-50/60')}>
                    {canModerate && (
                      <TableCell className="pe-0">
                        <Checkbox
                          checked={selected.has(r.id)}
                          onCheckedChange={(v) => toggleOne(r.id, Boolean(v))}
                          aria-label={`Select message from ${callerLabel}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-sm">
                      {isPlaying && audioOk && r.recording_id ? (
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="max-w-[8rem] truncate text-slate-600">
                            {callerLabel}
                          </span>
                          <div className="w-72 shrink-0">
                            <AudioPlayer
                              src={recordings.audioUrl(r.recording_id, accessToken)}
                              compact
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="truncate">{callerLabel}</span>
                      )}
                    </TableCell>
                    {!nodeId && (
                      <TableCell className="font-mono text-xs text-muted-foreground" title={r.ivr_node_name ?? ''}>
                        {forumLabel}
                      </TableCell>
                    )}
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={new Date(r.created_at).toLocaleString()}
                    >
                      {relativeWhen(r.created_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {durationLabel(r.recording_duration_ms)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} />
                    </TableCell>
                    <TableCell className="text-end font-mono text-xs tabular-nums text-muted-foreground">
                      {r.listen_count}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {audioOk && (
                          <button
                            type="button"
                            onClick={() => togglePlay(r.id)}
                            aria-label={isPlaying ? 'Stop playback' : 'Play message'}
                            className={cn(
                              'rounded p-1.5 transition-colors',
                              isPlaying
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                            )}
                          >
                            {isPlaying ? (
                              <Square className="h-3.5 w-3.5 fill-current" />
                            ) : (
                              <Play className="h-3.5 w-3.5 fill-current" />
                            )}
                          </button>
                        )}
                        {canModerate && r.status !== 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => approve.mutate({
                              id: r.id,
                              folderId: r.ivr_node_id ? folderByNode[r.ivr_node_id] ?? null : null,
                            })}
                            disabled={approve.isPending}
                            className="h-7 bg-emerald-700 px-2 hover:bg-emerald-800"
                            title="Approve message"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canModerate && r.status !== 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (!confirm('Reject this message? It will be hidden from listeners.')) return;
                              reject.mutate(r.id);
                            }}
                            disabled={reject.isPending}
                            className="h-7 border-rose-200 px-2 text-rose-700 hover:bg-rose-50"
                            title="Reject message"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canModerate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (!confirm('Delete this message permanently?')) return;
                              remove.mutate(r.id);
                            }}
                            disabled={remove.isPending}
                            aria-label="Delete message"
                            className="h-7 px-2"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function EmptyState({ tab }: { tab: TabKey }) {
  const label = tab === 'all' ? '' : tab;
  return (
    <div className="rounded-md border border-dashed border-slate-200 py-16 text-center">
      <Megaphone className="mx-auto mb-3 h-8 w-8 text-slate-300" />
      <p className="text-sm font-medium text-slate-500">
        No {label && `${label} `}forum posts to review
      </p>
      <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
        {tab === 'pending'
          ? 'Messages appear here when callers reach a public_forum IVR node.'
          : `Nothing in the ${label} bucket yet.`}
      </p>
    </div>
  );
}

function StatusBadge({ status }: { status: ModerationStatus }) {
  const styles: Record<ModerationStatus, string> = {
    pending:  'bg-amber-100 text-amber-900 ring-amber-200',
    approved: 'bg-emerald-100 text-emerald-900 ring-emerald-200',
    rejected: 'bg-rose-100 text-rose-900 ring-rose-200',
  };
  return (
    <span className={cn(
      'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1',
      styles[status],
    )}>
      {status}
    </span>
  );
}
